import { z } from 'zod';
import { PersonRef } from './people.js';
import { Priority, WorkItemType } from './work-items.js';
/**
 * The side panels beside chat (docs/adr/0077, Nathan 20 Oct 2026): a channel's tasks, and the work two people share in
 * a DM. Every list is over work the caller may see (docs/adr/0074), in the caller's own order.
 */
/** One lean task row: title, key, status, priority, due, assignee and comment count. */
export const PanelTask = z.object({
    id: z.uuid(),
    key: z.string(),
    type: WorkItemType,
    title: z.string(),
    statusId: z.uuid(),
    statusLabel: z.string(),
    statusColor: z.string(),
    priority: Priority,
    assignee: PersonRef.nullable(),
    dueDate: z.string().nullable(),
    commentCount: z.number().int(),
    projectId: z.uuid().nullable(),
    /** Empty when there is no project. */
    projectName: z.string(),
    done: z.boolean(),
    completedAt: z.string().nullable(),
});
/**
 * A group of open tasks. `project`: one project's tasks; `client`: the client's work without a project; `other`: other
 * open work for the same client (in a project channel); `channel`: tasks made in a team channel.
 */
export const PanelGroup = z.object({
    id: z.string(),
    kind: z.enum(['project', 'client', 'other', 'channel']),
    label: z.string(),
    projectId: z.uuid().nullable(),
    items: z.array(PanelTask),
});
/** A channel's task panel. Groups hold open tasks in the caller's order; `done` the ones finished lately. */
export const ChannelPanel = z.object({
    scope: z.enum(['project', 'client', 'team']),
    groups: z.array(PanelGroup),
    done: z.array(PanelTask),
    /** Open tasks across the groups. */
    open: z.number().int(),
    /** Whether the caller may create a task here (the New task button). */
    canCreate: z.boolean(),
});
/** A project both (or all) people in a DM are members of. */
export const PanelProject = z.object({
    id: z.uuid(),
    name: z.string(),
    key: z.string(),
    clientName: z.string(),
    /** The project's own channel when the caller may read it, to jump there. */
    channelId: z.uuid().nullable(),
    /** Open tasks in the project that the caller may see. */
    open: z.number().int(),
});
/**
 * A DM's side panel. `shared`: tasks between the people (assigned to them that the caller created, assigned to the
 * caller that they created). With none, `theirs`: what they are working on that the caller may see. `none`: neither.
 */
export const DmPanel = z.object({
    people: z.array(PersonRef),
    mode: z.enum(['shared', 'theirs', 'none']),
    tasks: z.array(PanelTask),
    done: z.array(PanelTask),
    projects: z.array(PanelProject),
});
/** The caller's own order of a panel's tasks, top first. Ids the caller can't see are ignored. */
export const PanelOrderInput = z.object({ itemIds: z.array(z.uuid()).max(500) });
export const PanelOrder = z.object({ itemIds: z.array(z.uuid()) });
//# sourceMappingURL=side-panel.js.map